import { useEffect, useState } from 'react';

import { notify } from '../../../app';
import { supportedLanguages } from '../../../utilities/languages';
import { useFetch, useModal } from '../../../hooks';
import { useRequest, useModels } from '../../../hooks';
import { getAgent, editAgent, editAgentFlow } from '../../../api';
import { formatAgentToForm, formatFormToAgent } from '../../../utilities';
import type { AgentResponse } from '../../../api';
import type { AgentForm } from '../../../utilities';
import { useModelsById } from '../../../components/AgentForm/utils/getModelById';
import { useSyncedModelSettings } from './useSyncedModelSettings';
import { useBackgroundAudioSettings } from './useBackgroundAudioSettings';
import { useKnowledgeBaseShortList } from '../../../hooks/useKnowledgeBase';
import { useAgentsOptions } from '../../../hooks/useAgentsOptions';

export const useAgent = (id: string) => {
  const [formId, setFormId] = useState(1);
  const { isOpened, onClose, onOpen } = useModal();
  const [form, setForm] = useState<AgentForm | null>(null);
  const [activeTab, setActiveTab] = useState('agentSettings');

  const { llmModels, ttsModels, sttModels, loadingModels } = useModels();
  const { knowledgeData, loading: loadingKnowledge } =
    useKnowledgeBaseShortList();
  const { agentsOptions, isAgentsLoading } = useAgentsOptions();

  const {
    ttsPrimary,
    ttsBackup,
    sttPrimary,
    sttBackup,
    llmPrimary,
    llmBackup,
    validity,
  } = useModelsById({
    form,
    ttsModels,
    sttModels,
    llmModels,
  });

  const [handleCopyFromAgent, copying] = useRequest(
    (_token, id: string) => getAgent(id),
    {
      deps: [],
      onSuccess: copyFromAgent => {
        setFormId(f => f + 1);
        setForm(old => ({
          ...formatAgentToForm(copyFromAgent),
          id: old?.id ?? '',
          title: old?.title ?? '',
        }));
        onClose();
      },
    }
  );

  const {
    intializing,
    data,
    refresh: refreshAgent,
    setData: setAgentData,
  } = useFetch(() => getAgent(id), [id], {
    onSuccess: newData => {
      setForm(formatAgentToForm(newData));
      setFormId(f => f + 1);
    },
  });
  const [handleUpdateAgent, editing] = useRequest(
    () => {
      if (!form) return null;
      const payload = formatFormToAgent(form);
      const mergedPayload = data?.pipeline_settings
        ? {
            ...payload,
            pipeline_settings: {
              ...data.pipeline_settings,
              ...payload.pipeline_settings,
              background_audio: {
                ...(data.pipeline_settings.background_audio ?? {}),
                ...(payload.pipeline_settings?.background_audio ?? {}),
              },
            },
          }
        : payload;

      return editAgent(id, mergedPayload);
    },
    {
      deps: [id, form, data],
      onSuccess: result => {
        if (!result) return notify.info('No changes to save');

        notify.success('Agent updated');
        refreshAgent();
      },
      onError: () => notify.error('Failed to update agent'),
    }
  );
  const [handleUpdateFlow, updatingFlow] = useRequest(
    async (_signal, conversation_flow: string, needMessage = false) => {
      await editAgentFlow(data?.id!, conversation_flow);

      setAgentData(old => {
        const thisOld: AgentResponse = old as any;

        return {
          ...thisOld,
          conversation_flow: conversation_flow
            ? JSON.parse(conversation_flow)
            : undefined,
        };
      });

      return { needMessage };
    },
    {
      deps: [data?.pipeline_settings, data?.id],
      onSuccess: ({ needMessage }) => {
        needMessage && notify.success('Flow saved');
      },
    }
  );
  useSyncedModelSettings({
    form,
    data,
    setForm,
    setFormId,
    models: {
      ttsPrimary,
      ttsBackup,
      sttPrimary,
      sttBackup,
      llmPrimary,
      llmBackup,
    },
  });

  useBackgroundAudioSettings({
    form,
    setForm,
    setFormId,
  });

  const handleCopy = (id: string) => handleCopyFromAgent(id);

  useEffect(() => {
    if (form?.rag_collection_id) {
      setForm(f => (f ? { ...f, is_rag_enabled: true } : null));
      setFormId(f => f + 1);
    } else {
      setForm(f => (f ? { ...f, is_rag_enabled: false } : null));
      setFormId(f => f + 1);
    }
  }, [form?.rag_collection_id]);

  return {
    showCopyModal: isOpened,
    openCopyModal: onOpen,
    closeCopyModal: onClose,
    activeTab,
    form,
    formId: String(formId),

    copying,
    updatingFlow,
    editing: editing || updatingFlow,
    loading: intializing || loadingModels || isAgentsLoading,

    agent: data,

    onChange: setForm,
    onCopy: handleCopy,
    onSubmit: handleUpdateAgent,
    setActiveAgentTab: setActiveTab,
    onUpdateFlow: handleUpdateFlow,
    ttsPrimary,
    ttsBackup,
    sttPrimary,
    sttBackup,
    llmPrimary,
    llmBackup,
    validity,
    languages: supportedLanguages,
    knowledgeData,
    loadingKnowledge,
    llmModels: llmModels || [],
    ttsModels: ttsModels || [],
    sttModels: sttModels || [],
    agentsOptions,
  };
};
